/** iter 42 — Mobile / Service-Attempt form.
 *
 * Two routes use this component:
 *   /m/jobs/:job_id/attempt/new        — create
 *   /m/attempts/:attempt_id            — view (read-only) with EDIT toggle
 *
 * "View-only with intent to be editable" — the same fields render in both
 * modes; only the disabled attribute and submit button differ.
 *
 * GPS lat/lng is auto-filled via the standard `navigator.geolocation` API
 * which Capacitor's WebView exposes natively. Photo and signature uploads
 * reuse the existing /api/uploads endpoints (Supabase Storage backed).
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { api } from "../../lib/api";
import MobileShell from "../MobileShell";

const OUTCOMES = ["Served", "Non-Service", "Bad Address", "Refused", "No Answer", "Other"];
const METHODS = ["Personal", "Sub-Service", "Posted", "Mail", "Corporate"];
const WEATHERS = ["Clear", "Cloudy", "Rain", "Snow", "Fog", "Other"];
const LIGHTINGS = ["Daylight", "Dusk", "Dawn", "Night", "Indoor"];
const DEMEANORS = ["Cooperative", "Indifferent", "Anxious", "Aggressive", "Refused contact"];

function Field({ label, children, testid }) {
  return (
    <div className="m-field" data-testid={testid}>
      <div className="m-field-label">{label}</div>
      {children}
    </div>
  );
}

const blank = {
  attempted_at: "",
  outcome: "",
  service_method: "",
  recipient_name: "",
  recipient_relationship: "",
  address_text: "",
  lat: "",
  lng: "",
  weather: "",
  lighting: "",
  demeanor: "",
  mileage: "",
  notes: "",
  photo_url: "",
  signature_url: "",
};

export default function MAttemptForm() {
  const { job_id, attempt_id } = useParams();
  const navigate = useNavigate();
  const mode = attempt_id ? "view" : "create";
  // For "create" mode, seed `attempted_at` to "now" at first render.
  const [form, setForm] = useState(() =>
    attempt_id ? blank : { ...blank, attempted_at: new Date().toISOString().slice(0, 16) },
  );
  const [editing, setEditing] = useState(mode === "create");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [parentJobId, setParentJobId] = useState(job_id || null);
  const sigRef = useRef(null);

  // iter 43 — proof-of-service options surfaced inline when outcome=Served.
  const [templates, setTemplates] = useState([]);
  const [notaryPool, setNotaryPool] = useState([]);
  const [proofOpts, setProofOpts] = useState({
    template_id: "",
    notary_choice: "default", // default | substitute | blank
    notary_substitute_user_id: "",
  });
  const [proof, setProof] = useState(null); // existing proof for this attempt

  useEffect(() => {
    // Templates + notary pool — only needed when user might trigger a Served proof.
    api.get("/affidavit-templates").then(r => setTemplates(r.data || [])).catch(() => {});
    api.get("/users?is_notary=true").then(r => setNotaryPool(r.data || [])).catch(() => {});
  }, []);

  // Load any existing proof for this attempt (view mode).
  useEffect(() => {
    if (!attempt_id) { setProof(null); return; }
    api.get(`/proofs?attempt_id=${attempt_id}`)
      .then(r => {
        const list = (r.data || []).filter(p => p.attempt_id === attempt_id);
        setProof(list[0] || null);
      })
      .catch(() => setProof(null));
  }, [attempt_id, form.outcome]);

  // ---- Load (view mode) ----------------------------------------------------
  useEffect(() => {
    if (mode !== "view") return;
    api.get(`/mobile/attempts/${attempt_id}`)
      .then(r => {
        setForm({ ...blank, ...r.data });
        setParentJobId(r.data.job_id);
      })
      .catch(e => setErr(e?.response?.data?.detail || e.message));
  }, [mode, attempt_id]);

  // ---- GPS capture ---------------------------------------------------------
  const capturePosition = () => {
    if (!navigator.geolocation) { setErr("Geolocation not available on this device"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm(f => ({
        ...f,
        lat: pos.coords.latitude.toFixed(6),
        lng: pos.coords.longitude.toFixed(6),
      })),
      (e) => setErr(`GPS error: ${e.message}`),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 },
    );
  };

  // ---- Photo upload --------------------------------------------------------
  const uploadFile = async (file, kind) => {
    if (!file) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const ep = kind === "signature" ? "/uploads/user/signature" : "/uploads/user/avatar";
      const r = await api.post(ep, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, [kind === "signature" ? "signature_url" : "photo_url"]: r.data.url }));
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  // ---- Signature pad upload (draws → png → upload) ------------------------
  const uploadSignature = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      setErr("Please draw your signature first");
      return;
    }
    setSaving(true);
    try {
      const dataUrl = sigRef.current.getCanvas().toDataURL("image/png");
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "signature.png", { type: "image/png" });
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/user/signature", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, signature_url: r.data.url }));
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  // ---- Submit --------------------------------------------------------------
  const save = async () => {
    setSaving(true); setErr("");
    try {
      const payload = { ...form };
      if (payload.lat === "") delete payload.lat; else payload.lat = parseFloat(payload.lat);
      if (payload.lng === "") delete payload.lng; else payload.lng = parseFloat(payload.lng);
      if (payload.mileage === "") delete payload.mileage; else payload.mileage = parseFloat(payload.mileage);
      if (payload.attempted_at === "") delete payload.attempted_at;

      // Iter 43 — proof options when outcome is "Served".
      if ((form.outcome || "").toLowerCase() === "served") {
        if (proofOpts.template_id) payload.proof_template_id = proofOpts.template_id;
        if (proofOpts.notary_choice === "substitute" && proofOpts.notary_substitute_user_id) {
          payload.proof_notary_substitute_user_id = proofOpts.notary_substitute_user_id;
        }
        if (proofOpts.notary_choice === "blank") payload.proof_leave_notary_blank = true;
      }

      if (mode === "create") {
        const r = await api.post(`/mobile/jobs/${parentJobId}/attempts`, payload);
        setEditing(false);
        navigate(`/m/attempts/${r.data.id}`, { replace: true });
      } else {
        await api.put(`/mobile/attempts/${attempt_id}`, payload);
        setEditing(false);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const ro = !editing;
  const inp = (k, type = "text") => (
    <input
      className="m-input"
      type={type}
      value={form[k] ?? ""}
      onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
      disabled={ro}
      data-testid={`m-attempt-${k}`}
    />
  );
  const sel = (k, opts) => (
    <select
      className="m-select"
      value={form[k] ?? ""}
      onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
      disabled={ro}
      data-testid={`m-attempt-${k}`}
    >
      <option value="">—</option>
      {opts.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  return (
    <MobileShell
      title={mode === "create" ? "New attempt" : "Attempt"}
      back={parentJobId ? `/m/jobs/${parentJobId}` : "/m/jobs"}
    >
      {err && <div className="m-card" style={{ background: "#fee2e2", color: "#991b1b" }} data-testid="m-attempt-error">{String(err)}</div>}

      <Field label="When"><input className="m-input" type="datetime-local" value={form.attempted_at?.slice(0, 16) || ""} onChange={e => setForm(f => ({ ...f, attempted_at: e.target.value }))} disabled={ro} data-testid="m-attempt-attempted_at" /></Field>
      <Field label="Outcome">{sel("outcome", OUTCOMES)}</Field>
      <Field label="Service method">{sel("service_method", METHODS)}</Field>

      <Field label="Recipient name">{inp("recipient_name")}</Field>
      <Field label="Recipient relationship">{inp("recipient_relationship")}</Field>

      <Field label="Address (as typed at door)">
        <textarea
          className="m-textarea"
          value={form.address_text ?? ""}
          onChange={e => setForm(f => ({ ...f, address_text: e.target.value }))}
          disabled={ro}
          data-testid="m-attempt-address_text"
        />
      </Field>

      <Field label="GPS lat / lng">
        <div className="m-row">
          {inp("lat")}
          {inp("lng")}
        </div>
        {editing && (
          <button type="button" className="m-btn" style={{ marginTop: 6 }} onClick={capturePosition} data-testid="m-attempt-capture-gps">
            CAPTURE GPS
          </button>
        )}
      </Field>

      <Field label="Weather">{sel("weather", WEATHERS)}</Field>
      <Field label="Lighting">{sel("lighting", LIGHTINGS)}</Field>
      <Field label="Demeanor">{sel("demeanor", DEMEANORS)}</Field>
      <Field label="Mileage at attempt">{inp("mileage", "number")}</Field>

      <Field label="Notes">
        <textarea
          className="m-textarea"
          value={form.notes ?? ""}
          onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          disabled={ro}
          data-testid="m-attempt-notes"
        />
      </Field>

      <Field label="Photo">
        {form.photo_url && (
          <img src={form.photo_url} alt="Attempt"
            style={{ maxWidth: "100%", borderRadius: 8, marginBottom: 6 }}
            data-testid="m-attempt-photo-preview" />
        )}
        {editing && (
          <input
            className="m-input"
            type="file"
            accept="image/*"
            capture="environment"
            onChange={e => uploadFile(e.target.files?.[0], "photo")}
            data-testid="m-attempt-photo-file"
          />
        )}
      </Field>

      <Field label="Signature">
        {form.signature_url && (
          <img src={form.signature_url} alt="Signature"
            style={{ maxWidth: "100%", borderRadius: 8, marginBottom: 6, background: "#fff" }}
            data-testid="m-attempt-signature-preview" />
        )}
        {editing && !form.signature_url && (
          <>
            <div style={{ border: "1px solid hsl(var(--border))", borderRadius: 8, background: "#fff" }}>
              <SignatureCanvas
                ref={sigRef}
                canvasProps={{ width: 320, height: 140, "data-testid": "m-attempt-signature-canvas" }}
                penColor="#000"
              />
            </div>
            <div className="m-row" style={{ marginTop: 6 }}>
              <button type="button" className="m-btn" onClick={() => sigRef.current?.clear()} data-testid="m-attempt-signature-clear">CLEAR</button>
              <button type="button" className="m-btn m-btn-primary" onClick={uploadSignature} data-testid="m-attempt-signature-save">SAVE SIGNATURE</button>
            </div>
          </>
        )}
      </Field>

      {/* iter 43 — Proof of Service options + status */}
      {(form.outcome || "").toLowerCase() === "served" && (
        <div className="m-card" data-testid="m-attempt-proof-section">
          <div className="m-card-title">Proof of Service</div>
          {proof ? (
            <>
              <div style={{ margin: "6px 0" }}>
                <span className="m-chip"
                  style={{ background: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))" }}
                  data-testid={`m-attempt-proof-status-${proof.status}`}
                >{proof.status?.replace("_", " ")}</span>
                {proof.notary_required && (
                  <span className="m-chip" style={{ marginLeft: 4 }}>
                    NOTARY {proof.notary_signed_at ? "✓" : "REQUIRED"}
                  </span>
                )}
              </div>
              <button type="button" className="m-btn"
                onClick={() => navigate(`/m/proof/${proof.id}`)}
                data-testid="m-attempt-open-proof"
              >OPEN PROOF</button>
            </>
          ) : (
            <div className="m-card-sub">Proof will auto-generate on save.</div>
          )}

          {editing && (
            <>
              <div className="m-field" style={{ marginTop: 10 }}>
                <div className="m-field-label">Affidavit template</div>
                <select className="m-select"
                  value={proofOpts.template_id}
                  onChange={e => setProofOpts(o => ({ ...o, template_id: e.target.value }))}
                  data-testid="m-attempt-proof-template"
                >
                  <option value="">— Use org default —</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.notary_required ? "  (NOTARY)" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="m-field">
                <div className="m-field-label">Notary</div>
                <select className="m-select"
                  value={proofOpts.notary_choice}
                  onChange={e => setProofOpts(o => ({ ...o, notary_choice: e.target.value }))}
                  data-testid="m-attempt-proof-notary-choice"
                >
                  <option value="default">Use my assigned notary</option>
                  <option value="substitute">Substitute another org notary…</option>
                  <option value="blank">Leave blank (manual / paper)</option>
                </select>
              </div>
              {proofOpts.notary_choice === "substitute" && (
                <div className="m-field">
                  <div className="m-field-label">Substitute notary</div>
                  <select className="m-select"
                    value={proofOpts.notary_substitute_user_id}
                    onChange={e => setProofOpts(o => ({ ...o, notary_substitute_user_id: e.target.value }))}
                    data-testid="m-attempt-proof-notary-sub"
                  >
                    <option value="">—</option>
                    {notaryPool.map(n => (
                      <option key={n.id} value={n.id}>
                        {n.full_name || n.email}{n.notary_state ? ` · ${n.notary_state}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {mode === "view" && !editing && (
        <button type="button" className="m-btn m-btn-primary" onClick={() => setEditing(true)} data-testid="m-attempt-edit-btn">
          EDIT
        </button>
      )}
      {editing && (
        <div className="m-row" style={{ marginTop: 8 }}>
          {mode === "view" && (
            <button type="button" className="m-btn" onClick={() => setEditing(false)} data-testid="m-attempt-cancel-btn">
              CANCEL
            </button>
          )}
          <button
            type="button"
            className="m-btn m-btn-primary"
            onClick={save}
            disabled={saving}
            data-testid="m-attempt-save-btn"
          >{saving ? "SAVING…" : (mode === "create" ? "SAVE ATTEMPT" : "SAVE CHANGES")}</button>
        </div>
      )}
    </MobileShell>
  );
}
